// src/shared/utils/refIdConflict.ts

import { bcLong } from '@lib2/model/util/string/BaseConverter';

/** Videos and streams share the mediaId space; playlists use listId. */
export type RefIdScope = 'media' | 'list';
export type RefIdAssetKind = 'video' | 'stream' | 'playlist';

/** Minimal structural inputs — VideoViewData / StreamViewData satisfy this. */
export interface RefIdMediaItem {
  media: { mediaId: number; refId: string | null; title: string };
}

/** Minimal structural input — CustomPlaylistView / TagPlaylistView satisfy this. */
export interface RefIdPlaylistItem {
  listId: number;
  refId?: string;
  title: string;
}

export interface RefIdOwner {
  kind: RefIdAssetKind;
  scope: RefIdScope;
  /** mediaId or listId, per scope. */
  id: number;
  /** Base-converted display id shown to users. */
  displayId: string;
  title: string;
}

export type RefIdIndex = Map<string, RefIdOwner[]>;

/**
 * Client-side reference-ID uniqueness checking. Reference IDs must be unique
 * per customer across videos, live streams, and playlists. Matching is exact
 * and case-sensitive. The backend refIdChanged validation remains the
 * authority — this is a UX layer over already-fetched list data.
 */
export class RefIdConflictFinder {
  private static readonly KIND_LABELS: Record<RefIdAssetKind, string> = {
    video: 'video',
    stream: 'live stream',
    playlist: 'playlist',
  };

  /** Single O(n) pass over all sources; items without a refId are skipped. */
  static buildIndex(sources: {
    videos?: readonly RefIdMediaItem[];
    streams?: readonly RefIdMediaItem[];
    playlists?: readonly RefIdPlaylistItem[];
  }): RefIdIndex {
    const index: RefIdIndex = new Map();

    const add = (
      refId: string | null | undefined,
      kind: RefIdAssetKind,
      scope: RefIdScope,
      id: number,
      title: string,
    ): void => {
      if (!refId) return;
      const owner: RefIdOwner = { kind, scope, id, displayId: bcLong.getStr(id), title };
      const bucket = index.get(refId);
      if (bucket) {
        bucket.push(owner);
      } else {
        index.set(refId, [owner]);
      }
    };

    for (const v of sources.videos ?? []) {
      add(v.media.refId, 'video', 'media', v.media.mediaId, v.media.title);
    }
    for (const s of sources.streams ?? []) {
      add(s.media.refId, 'stream', 'media', s.media.mediaId, s.media.title);
    }
    for (const p of sources.playlists ?? []) {
      add(p.refId, 'playlist', 'list', p.listId, p.title);
    }
    return index;
  }

  /** O(1): first owner of refId that is not the requesting asset itself. */
  static findConflict(
    index: RefIdIndex,
    refId: string,
    selfScope: RefIdScope,
    selfId: number,
  ): RefIdOwner | null {
    if (!refId) return null;
    const owners = index.get(refId);
    if (!owners) return null;
    return owners.find((o) => !(o.scope === selfScope && o.id === selfId)) ?? null;
  }

  static conflictMessage(owner: RefIdOwner): string {
    return `Already used by ${this.KIND_LABELS[owner.kind]} "${owner.title}" (${owner.displayId})`;
  }
}
